import axios from "axios";
import HorizontalScroll from "@Components/HorizontalScroll";
import Image from "@Components/Image";
import Button from "@Components/Button";
import config from "@Config/index";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import wrapperHOC from "@Utils/wrapperHOC";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import useIsLanguage from "hooks/useIsLanguage";
import { SCREEN_SIZE, useResponsiveness } from "hooks/useResponsiveness";
import useTranslationHook from "hooks/useTranslationHook";
import { UIEvent, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Subsection from "../Subsection";
import styles from "./AmberPlusOffers.module.scss";

type AmberPlusOffersProps = {
  isDesktop?: boolean;
  greyBg?: boolean;
  className?: string;
};

type CmsOffer = {
  offer?: {
    label?: string;
    title?: string;
    subtitle?: string;
    redirectLink?: string;
    image?: {
      url?: string;
      alt?: string;
    };
    partner?: {
      logo?: {
        url?: string;
        alt?: string;
      };
    };
  };
  heroImage?: {
    url?: string;
    alt?: string;
  };
};

let cachedOffers: CmsOffer[] | null = null;
let offersRequest: Promise<CmsOffer[]> | null = null;

const PRIMARY_AMBER_PLUS_BANNER = {
  href: "/plus?utm_source=home_page",
  desktopBg: `${config.IMAGE_STATIC_ASSETS_APP_PATH}/assets/Offer-Card-03.png?q=90`,
  mobileBg: `${config.IMAGE_STATIC_ASSETS_APP_PATH}/assets/refer3.png?q=90`,
};

const getCmsUrl = (path: string) => `${config.CMS_API_URL}${path}`;

const getPartnerName = (label = "") => label.split("—")[0].trim() || "Amber+";
const getOfferCategory = (label = "") => label.split("—")[1]?.trim() || "General";
const getPartnerLogo = (item: CmsOffer) =>
  item.offer?.partner?.logo || item.offer?.image || { url: "", alt: "" };

const fetchAmberPlusOffers = async (): Promise<CmsOffer[]> => {
  if (cachedOffers) return cachedOffers;
  if (offersRequest) return offersRequest;

  offersRequest = axios
    .get(getCmsUrl("/api/amberplus-homepage-featured?depth=3&draft=false"))
    .then((response) => {
      const items = response?.data?.docs?.[0]?.items || [];
      cachedOffers = items;
      return items;
    })
    .catch(() => [])
    .finally(() => {
      offersRequest = null;
    });

  return offersRequest;
};

const AmberPlusOffers = ({
  isDesktop = false,
  greyBg = true,
  className = "",
}: AmberPlusOffersProps) => {
  const { t } = useTranslationHook();
  const language = useIsLanguage();
  const { isAbove } = useResponsiveness();
  const isAboveMD = isAbove(SCREEN_SIZE.MD, false);
  const showForDesktop = isDesktop && isAboveMD;
  const [offers, setOffers] = useState<CmsOffer[]>([]);
  const sectionRef = useRef<HTMLDivElement>(null);
  const viewedCardDepthRef = useRef(1);
  const hasSentViewedEventRef = useRef(false);
  const sectionTitle = (
    <>
      Amber<span className={styles["brand-plus"]}>+</span> Offers
    </>
  );

  useEffect(() => {
    let active = true;
    const fetchOffers = async () => {
      const items = await fetchAmberPlusOffers();
      if (active) setOffers(items);
    };

    fetchOffers();
    return () => {
      active = false;
    };
  }, []);

  const validOffers = useMemo(
    () => offers.filter((item) => Boolean(item.heroImage?.url && item.offer?.redirectLink)),
    [offers],
  );
  const totalCards = 1 + validOffers.length;
  const platform = showForDesktop ? "web" : "mobile";

  useLayoutEffect(() => {
    if (totalCards < 1 || hasSentViewedEventRef.current) {
      return undefined;
    }
    const el = sectionRef.current;
    if (!el) {
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry?.isIntersecting || hasSentViewedEventRef.current) return;
        hasSentViewedEventRef.current = true;
        gtagEventEmitter({
          action: "amber_plus_section_viewed",
          platform,
        });
        observer.disconnect();
      },
      { threshold: 0.2, rootMargin: "0px" },
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [platform, totalCards, offers.length]);

  const emitScrollDepth = (maxViewedCard: number) => {
    gtagEventEmitter({
      action: "amber_plus_carousel_scroll",
      scroll_depth: `viewed ${maxViewedCard} out of ${totalCards} cards`,
    });
  };

  const handleCarouselScroll = (event: UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    if (!target) return;
    const cardWidth = showForDesktop ? 500 : 314;
    const visibleCard = Math.min(
      totalCards,
      Math.max(1, Math.ceil((target.scrollLeft + target.clientWidth) / cardWidth)),
    );
    if (visibleCard <= viewedCardDepthRef.current) return;
    viewedCardDepthRef.current = visibleCard;
    emitScrollDepth(visibleCard);
  };

  const handleOfferClick = (offerName: string, offerIndex: number, offerCategory: string) => {
    gtagEventEmitter({
      action: "amber_plus_offer_clicked",
      offer_name: offerName,
      offer_index: offerIndex,
      offer_category: offerCategory,
    });
  };

  if (!offers.length) return null;

  return (
    <div ref={sectionRef}>
      <Subsection
        title={sectionTitle}
        subtitle="Access travel essentials and services designed to make your move seamless."
        withSidePadding={showForDesktop}
        subSectionPaddingReduced={!showForDesktop}
        greyBg={greyBg}
        isDesktop={showForDesktop}
        className={className}
        noPaddingTop
        largeMarginBottom
      >
        <HorizontalScroll
          showControls={showForDesktop}
          className={formatClassNames(
            styles["horizontal-scroll"],
            showForDesktop && styles.desktop,
          )}
          controlsClass={styles["controls-full-height"]}
          itemSize={showForDesktop ? 500 : 314}
          onHorizontalScroll={handleCarouselScroll}
        >
          <a
            href={PRIMARY_AMBER_PLUS_BANNER.href}
            target="_blank"
            rel="noreferrer"
            className={formatClassNames(styles["offer-card"], styles["first-offer-card"])}
            onClick={() => handleOfferClick("Amber+", 1, "General")}
          >
            <Image
              src={
                showForDesktop
                  ? PRIMARY_AMBER_PLUS_BANNER.desktopBg
                  : PRIMARY_AMBER_PLUS_BANNER.mobileBg
              }
              alt="amber plus"
              width={showForDesktop ? 500 : 314}
              height={showForDesktop ? 200 : 170}
              className={styles["banner-image"]}
              isEagerLoad
            />
            <div className={styles.overlay} />
            <div className={styles["first-content"]}>
              <div>
                <h3 className={formatClassNames(styles.title, styles["first-title"])}>
                  {language !== "en"
                    ? t("home.reffralprogram.amberplus.title")
                    : "Save up to £1500 with amber+"}
                </h3>
                <p className={formatClassNames(styles.subtitle, styles["first-subtitle"])}>
                  {t("home.reffralprogram.amberplus.subtitleone")}
                  <br />
                  {t("home.reffralprogram.amberplus.subtitletwo")}
                </p>
              </div>
              <Button variant="primary" size="md" className={styles["first-cta"]}>
                Explore Services
              </Button>
            </div>
          </a>
          {validOffers.map((item: CmsOffer, index: number) => {
            const backgroundUrl = item.heroImage?.url || "";
            const title = item.offer?.title || "";
            const subtitle = item.offer?.subtitle || "";
            const redirectLink = item.offer?.redirectLink || "";
            const partnerName = getPartnerName(item.offer?.label);
            const offerCategory = getOfferCategory(item.offer?.label);
            const partnerLogo = getPartnerLogo(item);

            return (
              <a
                key={`${partnerName}-${index}`}
                href={redirectLink}
                target="_blank"
                rel="noreferrer"
                className={styles["offer-card"]}
                onClick={() => handleOfferClick(partnerName, index + 2, offerCategory)}
              >
                <Image
                  src={backgroundUrl}
                  alt={item.heroImage?.alt || partnerName}
                  width={showForDesktop ? 500 : 314}
                  height={showForDesktop ? 200 : 170}
                  className={styles["banner-image"]}
                  isEagerLoad={index < 2}
                />
                <div className={styles.overlay} />
                <div className={styles["logo-pill"]}>
                  {partnerLogo?.url ? (
                    <Image
                      src={partnerLogo.url}
                      alt={partnerLogo.alt || partnerName}
                      width={showForDesktop ? 96 : 84}
                      height={showForDesktop ? 28 : 24}
                      className={styles["logo-image"]}
                      isEagerLoad={index < 2}
                    />
                  ) : (
                    partnerName
                  )}
                </div>
                <div className={styles.content}>
                  <h3 className={styles.title}>{title}</h3>
                  <p className={styles.subtitle}>{subtitle}</p>
                </div>
              </a>
            );
          })}
        </HorizontalScroll>
      </Subsection>
    </div>
  );
};

export default wrapperHOC(AmberPlusOffers, {
  componentName: "AmberPlusOffers-Home",
  showForChina: true,
});
